import { inject } from '@angular/core'

import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'

import { lastValueFrom } from 'rxjs'

import { ApplicationResource } from '@flowx/designer/shared/entities/application.entity'
import { ContentValue, Enumeration } from '@flowx/designer/shared/entities/enumeration.entity'
import { EnumerationsService } from '@flowx/designer/shared/services/enumerations.service'

export interface Language {
  id?: string
  name: string
  values: string
}

type SubstitutionTagsStore = {
  substitutionTagsAR: ApplicationResource
  substitutionTagsEnumeration: Enumeration
  loading: boolean
}

const initialState: SubstitutionTagsStore = {
  substitutionTagsAR: null,
  substitutionTagsEnumeration: null,
  loading: false,
}

export const SubstitutionTagsStore = signalStore(
  withState(initialState),
  withMethods((store, enumerationsService = inject(EnumerationsService)) => ({
    getAllSubstitutionTags: async () => {
      patchState(store, { loading: true })
      try {
        const paginatedResponseEnumeration = await lastValueFrom(
          enumerationsService.getSubstitutionEnumeration()
        )
        if (paginatedResponseEnumeration.content.length === 0) {
          console.error('No substitution tags enumeration found')
          patchState(store, { loading: false })
          return
        }
        const [firstKey] = Object.keys(paginatedResponseEnumeration.content)
        if (!firstKey.length) {
          console.error('No substitution tags enumeration found')
          patchState(store, { loading: false })
          return
        }
        const substitutionTagsAR = paginatedResponseEnumeration.content[firstKey][0]
        const enumerationValues = await lastValueFrom(
          enumerationsService.getSubstitutionValues(substitutionTagsAR.resourceId)
        )
        if (enumerationValues.id !== substitutionTagsAR.resourceId) {
          substitutionTagsAR.resourceId = enumerationValues.id
        }
        patchState(store, {
          substitutionTagsAR,
          substitutionTagsEnumeration: enumerationValues,
          loading: false,
        })
      } catch (error) {
        patchState(store, { loading: false })
        throw error
      }
    },
    createSubstitutionTags: async (enumContentValue: Partial<ContentValue>) => {
      patchState(store, { loading: true })
      try {
        const response = await lastValueFrom(
          enumerationsService.createSubstitutionTag(store.substitutionTagsAR(), enumContentValue)
        )
        const newSubstitutionTags: Enumeration = {
          ...store.substitutionTagsEnumeration(),
          contentValues: [response, ...store.substitutionTagsEnumeration().contentValues],
        }
        patchState(store, { substitutionTagsEnumeration: newSubstitutionTags, loading: false })
      } catch (error) {
        patchState(store, { loading: false })
        throw error
      }
    },
    updateSubstitutionTags: async (enumContentValue: Partial<ContentValue>) => {
      const response = await lastValueFrom(
        enumerationsService.createSubstitutionTag(store.substitutionTagsAR(), enumContentValue)
      )
      const newSubstitutionTags: Enumeration = {
        ...store.substitutionTagsEnumeration(),
        contentValues: [
          ...store.substitutionTagsEnumeration().contentValues.map((value) => {
            if (value.valueId === response.valueId) {
              return response
            }
            return value
          }),
        ],
      }
      patchState(store, { substitutionTagsEnumeration: newSubstitutionTags })
    },
    deleteSubstitutionTag: async (valueId: string) => {
      enumerationsService.deleteSubstitutionValue(
        store.substitutionTagsAR().resourceId,
        store.substitutionTagsAR(),
        valueId
      )
      const newSubstitutionTags: Enumeration = {
        ...store.substitutionTagsEnumeration(),
        contentValues: [
          ...store
            .substitutionTagsEnumeration()
            .contentValues.filter((value) => value.valueId !== valueId),
        ],
      }
      patchState(store, { substitutionTagsEnumeration: newSubstitutionTags })
    },
  }))
)
